<template>
  <div
    class="cmp-login-section fit column no-wrap justify-between ws-typography"
  >
    <!--    LOGO   -->
    <div class="login-header row">
      <img alt="logo" v-bind="logo" />
    </div>
    <!--    FIRST AND ONLY SCREEN FOR CEE  -->
    <div v-if="!bankIdAllowed" class="cmp-login-section__first-step">
      <div class="section login-content row">
        <div class="col">
          <h6>{{ $t('login.existing.title') }}</h6>
          <small class="block q-mb-lg">{{ $t('login.existing.text') }}</small>
          <div>
            <ws-button
              :type="WsButtonEnum.Type.PRIMARY"
              :size="WsButtonEnum.Size.XL"
              :loading="loadingRedirect"
              :label="$t('login.existing.btn')"
              full-width
              @click="goToLogin"
            />
          </div>
          <ws-delimiter text="wsk.general.or" class="q-my-lg" />
          <h6 class="second-title">
            {{ $t('login.new.title') }}
          </h6>
          <small class="block q-mb-lg">{{ $t('login.new.text') }}</small>
          <ws-button
            :type="WsButtonEnum.Type.SECONDARY"
            :size="WsButtonEnum.Size.XL"
            :loading="loadingGoNext"
            :label="$t('login.new.btn')"
            class="cy--login-section--register"
            full-width
            @click="goNext"
          />
        </div>
      </div>
    </div>

    <!--    SECOND STEP SCREEN FOR SE   -->
    <feature-wrapper v-if="bankIdAllowed" :feature="Features.BankIdLogin">
      <slot>
        <ws-button
          :type="WsButtonEnum.Type.TERTIARY_NEUTRAL"
          :size="WsButtonEnum.Size.SMALL"
          :loading="loadingGoNext"
          :label="$t('login.new.manual')"
          class="cy--login-section--register"
          full-width
          @click="goNext"
        />
      </slot>
    </feature-wrapper>
    <!--    FOOTER    -->
    <div class="login-footer">
      <hr class="ws-default" />

      <div class="row content items-center">
        <div v-if="footerImage && footerImage.src" class="col-auto q-pr-xl gt-sm">
          <img alt="" v-bind="footerImage" />
        </div>
        <div class="col footer-content">
          <h6 class="ws-typo-button">
            {{ $t('login.footer.title') }}
          </h6>

          <small>
            {{ $t('login.footer.text1') }}
            <a class="ws-link" :href="footerTextUrl" target="_blank">{{
              $t('login.footer.link')
            }}</a>
            {{ $t('login.footer.text2') }}
          </small>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { mapGetters } from 'vuex';
import { QBtn } from 'quasar';

import { redirect } from '@loopia-group/utils';
import WsDelimiter from '@WS_Components/WsDelimiter.vue';
import { WsButton, WsButtonEnum } from '@loopia-group/components-vue2';
import { STEPS } from '@/services/const.enum';
import { getLoginUrl } from '@/utilities';
import config, { type AppConfig } from '@/services/config.service';
import { navigateToStep } from '@/services/order.service';
import logoWebsupportSvg from '@/assets/images/ws-logo-full.svg';
import LogoA24Svg from '@/assets/images/A24/a24-logo-black.svg';
import LogoLoopiaSvg from '@/assets/images/loopia-logo-full.svg';
import A24loginFooterSvg from '@/assets/images/A24/a24-footer-icon.svg';
import loginFooterImg from '@/assets/images/Cary/cary-help.png';
import {StoreActions} from '@/store/const.enum';
import {ROUTENAMES} from '@/consts/const.enum';
import { Theme } from '@loopia-group/services';
import { computed } from 'vue';
import FeatureWrapper from '@/components/FeatureWrapper.vue';
import {Features, useFeatureFlag} from '@/services/featureflag.service.ts';

@Component({
  components: {
    FeatureWrapper,
    WsDelimiter,
    WsButton,
    QBtn,
  },
  computed: {
    ...mapGetters(['orderSteps']),
  },
})
export default class LoginSection extends Vue {
  orderSteps!: STEPS[];
  config: AppConfig = config;
  Features = Features;
  footerTextUrl = config.supportUrl || 'https://www.google.com';
  loadingRedirect = false;
  loadingGoNext = false;
  WsButtonEnum = WsButtonEnum;
  bankIdAllowed = false;

  data() {
    const currentTheme = (this as any).currentTheme();

    const getImages = computed(() => {
      switch (currentTheme) {
      case Theme.ACTIVE_24:
        return {
          logo: {
            'src': LogoA24Svg,
            'alt': 'Active24 logo',
            'class': 'q-mb-sm',
          },
          footerImage: {
            'src': A24loginFooterSvg,
            'alt': 'Active24 placeholder',
            'class': 'footer-img',
          }};
      case Theme.LOOPIA:
        return {
          logo: {
            'src': LogoLoopiaSvg,
            'alt': 'Loopia logo',
            'class': 'q-mb-sm',
          },
          footerImage: {}
        };
      case Theme.CARY:
      default:
        return {
          logo: {
            'src': logoWebsupportSvg,
            'alt': 'Websupport logo',
            'class': 'logo',
          },
          footerImage: {
            'srcset': loginFooterImg,
            'src': loginFooterImg,
            'alt': 'heroes',
            'class': 'footer-img',
          }
        };
      }
    });

    const { logo, footerImage } = getImages.value;
    return {
      ROUTENAMES,
      logo,
      footerImage
    };
  }

  goToLogin() {
    this.loadingRedirect = true;
    // TODO: parametrize redirect url from outside somehow
    if (this.$route.name === ROUTENAMES.DOMAIN_CHECKER) {
      redirect(getLoginUrl(this.$router, ROUTENAMES.DOMAIN_CHECKER));
    } else {
      // return back to CART (https://loopiagroup.atlassian.net/browse/PNO-2156)
      redirect(getLoginUrl(this.$router, ROUTENAMES.CART));
    }
  }

  goNext() {
    // not initiating routing to next page on 'Create new user' button click
    // instead showing the Qcode
    if (useFeatureFlag(Features.BankIdLogin) && !this.bankIdAllowed) {
      this.bankIdAllowed = true;
      return;
    }

    this.loadingGoNext = true;
    this.$store.dispatch(StoreActions.SET_USER_STEP, {
      step: this.orderSteps[1],
    });
    navigateToStep(this.orderSteps[1]).finally(
      () => (this.loadingGoNext = false)
    );
  }
}
</script>

<style lang="scss" scoped>
.cmp-login-section {
  .logo {
    height: 64px;
  }

  .login-header .row {
    padding: 0;
  }
  .login-content {
    padding: 16px 0;
  }
  .login-content .second-title {
    margin-top: 12px;
  }

  .login-footer .content {
    padding-top: map-get($space-xl, y);
    padding-bottom: 0;
  }

  .footer-img {
    max-width: 135px;
  }
}
</style>
